import React, { useMemo, useState } from 'react';
import snakeToPascal, { round } from '../../tools';
import { useAppStore } from '../../shared/stores/useAppStore';
import OutputTimeSeriesGridOperational from '../ModelDataFields/Output/TimeSeriesGridOperational';
import { TailwindButton } from '../Tailwind';
import { FMEPageHeader, useModelContext } from '../Layout';
import ValuationExport from './ValuationExport';
import OperationalScenarioSelector from './OperationalScenarioSelector';

interface OperationalValuationPageProps {
    model: any;
    currentScenario: any;
    currentSensitivity: any;
    valuationState: any;
    computeValuation: () => Promise<void>;
    reloadState: () => void;
    userRights: any;
}

function formatFloatValue(field: any): string {
    const rawValue = field.is_percentage ? field.currentValue.value * 100 : field.currentValue.value;
    if (rawValue == null) return '—';
    if (field.roundable) {
        return round(rawValue, useAppStore.getState().userSettings.roundPrecision).toLocaleString(
            useAppStore.getState().userSettings.numberFormattingLocale,
            { maximumFractionDigits: useAppStore.getState().userSettings.roundPrecision }
        );
    }
    return typeof rawValue === 'number'
        ? rawValue.toLocaleString(useAppStore.getState().userSettings.numberFormattingLocale)
        : String(rawValue);
}

function getSuffix(field: any): string {
    if (field.is_percentage) return '%';
    return field.suffix || '';
}

export default function OperationalValuationPage({
    model,
    currentScenario,
    currentSensitivity,
    valuationState,
    computeValuation,
    reloadState,
    userRights,
}: OperationalValuationPageProps) {
    const [computing, setComputing] = useState(false);
    const { isComputed, auditPassed } = useModelContext();

    const canCompute = isComputed !== false && auditPassed === true;

    const tree = valuationState?.Output?.tree;
    const rootItem = tree?.items?.[tree?.rootId];

    const valuationItems = useMemo(() => {
        if (!tree?.items || !rootItem) return [];
        const items: any[] = [];
        const collectItems = (itemId: string, level: number) => {
            const item = tree.items[itemId];
            if (!item) return;
            if (item.id !== tree.rootId) {
                items.push({ ...item, level });
            }
            (item.childrens || []).forEach((childId: string) => collectItems(childId, level + 1));
        };
        (rootItem.childrens || []).forEach((childId: string) => collectItems(childId, 0));
        return items;
    }, [tree, rootItem]);

    // Separate KPI float fields from time series items
    const { kpiFields, timeSeriesItems } = useMemo(() => {
        const kpis: { name: string; displayName: string; field: any; icon: string }[] = [];
        const tsItems: any[] = [];

        for (const item of valuationItems) {
            const scalarEntries = Object.entries(item.fields || {}).filter(
                ([_, f]: [string, any]) => (f.type === 'float' || f.type === 'grid') && f.currentValue?.value != null
            );
            const hasTimeSeries = Object.values(item.fields || {}).some(
                (f: any) => f.type === 'time_serie'
            );

            for (const [fieldName, field] of scalarEntries as [string, any][]) {
                kpis.push({
                    name: fieldName,
                    displayName: field.display_name || snakeToPascal(fieldName),
                    field,
                    icon: fieldName.toLowerCase().includes('rate') ? 'percent' : 'analytics',
                });
            }

            if (hasTimeSeries) {
                tsItems.push(item);
            }
        }

        return { kpiFields: kpis, timeSeriesItems: tsItems };
    }, [valuationItems]);

    const handleCompute = async () => {
        setComputing(true);
        try {
            await computeValuation();
        } finally {
            setComputing(false);
        }
    };

    return (
        <div className="flex flex-col h-full">
            <FMEPageHeader
                title="Valuation Results"
                breadcrumb={[model.name, currentScenario.name, ...(currentSensitivity ? [currentSensitivity.name] : [])]}
                description="Valuation is computed using the selected scenario data, with values overridden by realized inputs from the BPO."
                actions={<OperationalScenarioSelector />}
            />

            <div className="flex-1 overflow-y-auto">
                {/* Action buttons (Export + Compute) */}
                <div className="flex flex-col items-end px-8 pt-6 pb-4 gap-2">
                    <div className="flex items-center gap-2">
                        <ValuationExport
                            scenarioId={currentScenario.id}
                            scenarioName={currentScenario.name}
                            disabled={kpiFields.length === 0}
                        />
                        <TailwindButton
                            variant="primary"
                            icon={computing ? undefined : 'memory'}
                            onClick={handleCompute}
                            disabled={computing || !canCompute}
                            className="h-9 px-4 bg-slate-900 hover:bg-slate-800 text-white text-sm"
                        >
                            {computing ? (
                                <div className="flex items-center gap-2">
                                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Computing...
                                </div>
                            ) : (
                                'Compute'
                            )}
                        </TailwindButton>
                    </div>
                    {!canCompute && (
                        <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
                            <span className="material-symbols-outlined text-[18px]">warning</span>
                            <span>
                                {isComputed === false && 'The scenario must be computed before running valuation.'}
                                {isComputed !== false && auditPassed === false && 'The scenario audit must pass before running valuation.'}
                                {isComputed !== false && auditPassed === null && 'The scenario audit must be run before running valuation. (Incomplete scenario)'}
                            </span>
                        </div>
                    )}
                </div>

                {valuationItems.length === 0 ? (
                    <div className="mx-8 border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                        <span className="material-symbols-outlined text-[48px] text-slate-400 mb-2">monitoring</span>
                        <p className="text-slate-500">No valuation results available. Click Compute Valuation to generate.</p>
                    </div>
                ) : (
                    <div className="space-y-6 pb-6">
                        {/* Hero KPI cards */}
                        {kpiFields.length > 0 && (
                            <div className="px-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {kpiFields.map((kpi) => (
                                    <div
                                        key={kpi.name}
                                        className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">
                                                {kpi.displayName}
                                            </span>
                                            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 text-primary">
                                                <span className="material-symbols-outlined text-[20px]">{kpi.icon}</span>
                                            </div>
                                        </div>
                                        <div className="flex items-baseline gap-1.5">
                                            <span className="text-4xl font-extrabold tracking-tight text-slate-900 tabular-nums">
                                                {formatFloatValue(kpi.field)}
                                            </span>
                                            {getSuffix(kpi.field) && (
                                                <span className="text-xl font-semibold text-slate-400">
                                                    {getSuffix(kpi.field)}
                                                </span>
                                            )}
                                        </div>
                                        {kpi.field.compute_error && (
                                            <div className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded mt-3 inline-flex items-center gap-1">
                                                <span className="material-symbols-outlined text-[14px]">warning</span>
                                                {kpi.field.compute_error}
                                            </div>
                                        )}
                                        <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-primary/50 via-primary/25 to-transparent" />
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Time Series */}
                        {timeSeriesItems.map((item) => (
                            <div key={item.id}>
                                <div className="flex-1 overflow-auto relative border-t border-b border-slate-200">
                                    <OutputTimeSeriesGridOperational
                                        fields={item.fields}
                                        containerClassName="overflow-auto"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
